import { useContext, useEffect, createContext, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/SupabaseAuthProvider';
import { AvailableRoles, ComponentWithChildrenType, SystemUser } from '@/types';
import { Role } from '@/types/api';

type UserContext = {
  profile: SystemUser;
  isAdmin?: boolean;
  refresh: () => void;
};

const defaultState: UserContext = {
  profile: {
    auth0Id: null,
    createdAt: '',
    deletedAt: null,
    email: '',
    id: 'test',
    name: '',
    personId: null,
    person: null,
    roles: [],
    slackId: null,
    updatedAt: '',
    isLoading: true,
  },
  refresh: () => {
    return undefined;
  },
};

export const userContext = createContext<UserContext>(defaultState);

const getUsername = (_user: SystemUser): string => {
  if (!_user) return '';
  return _user?.name ?? _user?.nickname ?? '';
};

const getInitials = (_user: SystemUser): string => {
  if (!_user) return '';

  if (_user.given_name && _user.family_name) return `${_user.given_name[0]}${_user.family_name[0]}`;

  const userNameTwoFirstLetters = getUsername(_user).slice(0, 2);
  return userNameTwoFirstLetters;
};

export function UserProvider({ children }: ComponentWithChildrenType): JSX.Element {
  const { user: authUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const [user, setUser] = useState<SystemUser>({ ...defaultState.profile });

  const fetchProfile = useCallback(async () => {
    if (!authUser) return;

    try {
      // Fetch profile from Supabase
      let { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // No profile linked by auth_user_id — try matching by email or work_email and link them
        const authEmail = authUser.email?.toLowerCase();
        if (authEmail) {
          const { data: emailProfile } = await supabase
            .from('profiles')
            .select('*')
            .or(`email.ilike.${authEmail},work_email.ilike.${authEmail}`)
            .is('deleted_at', null)
            .maybeSingle();

          if (emailProfile) {
            // Link this profile to the auth user
            await supabase
              .from('profiles')
              .update({ auth_user_id: authUser.id })
              .eq('id', emailProfile.id);
            profile = { ...emailProfile, auth_user_id: authUser.id };
            error = null;
          }
        }
      }

      if (error) {
        if (error.code === 'PGRST116') {
          // Profile not found - user not invited
          setUser((currentUser) => ({ ...currentUser, isNotInvited: true, isLoading: false }));
          return;
        }
        throw error;
      }

      // Fetch user roles
      const { data: profileRoles } = await supabase
        .from('profile_roles')
        .select(
          `
          role_id,
          roles (
            id,
            value,
            role_permissions (
              permissions (
                id,
                value
              )
            )
          )
        `
        )
        .eq('profile_id', profile.id);

      const roles: Role[] = (profileRoles ?? []).map((pr: any) => ({
        id: pr.roles.id,
        value: pr.roles.value,
        permissions: (pr.roles.role_permissions ?? []).map((rp: any) => ({
          id: rp.permissions.id,
          value: rp.permissions.value,
        })),
      }));

      const fullName = `${profile.first_name} ${profile.last_name}`;

      // Detect if user needs onboarding (invited but hasn't set up their profile)
      const isNameEmpty =
        !profile.first_name || profile.first_name === profile.email?.split('@')[0];
      const needsOnboarding = isNameEmpty && !profile.last_name;

      // Build legacy PersonType for backward compatibility
      const person = {
        id: profile.id,
        firstName: profile.first_name,
        lastName: profile.last_name,
        email: profile.email,
        mobilePhone: profile.phone || '',
        pictureUrl: profile.picture_url,
        birthdate: profile.birthdate,
        gender: profile.gender,
        ethnicity: profile.ethnicity,
        linkedinUrl: profile.linkedin_url || '',
        githubUrl: profile.github_url,
        city: profile.city || '',
        state: profile.state,
        country: profile.country,
        addressLine1: profile.address_line1,
        addressLine2: profile.address_line2,
        zipCode: profile.zip_code,
        workEmail: profile.work_email,
        summary: profile.summary,
        emergencyContactName: profile.emergency_contact_name,
        emergencyContactPhone: profile.emergency_contact_phone,
        emergencyContactEmail: profile.emergency_contact_email,
        emergencyContactRelationship: profile.emergency_contact_relationship,
        developerType: profile.developer_type,
        availability: profile.availability,
        experience: profile.experience || '',
        englishLevel: profile.english_level,
        salaryExpectation: profile.salary_expectation || '',
        source: profile.source || '',
        referredBy: profile.referred_by || '',
        status: profile.status,
        rating: profile.overall_rating || 0,
        overallRating: profile.overall_rating || 0,
        technicalSkillsRating: profile.technical_rating || 0,
        softSkillsRating: profile.soft_skills_rating || 0,
        communicationSkillsRating: profile.communication_rating || 0,
        isTalentPool: profile.is_talent_pool || false,
        hasFintechExperience: profile.has_fintech_experience || false,
        trioProfileUrl: profile.trio_profile_url,
        ownerId: profile.owner_id || '',
        candidateOwner: null,
        owner: null,
        employeeWorkStatus: profile.employee_work_status,
        skills: [],
        mainTechStack: [],
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
        deletedAt: profile.deleted_at,
      };

      const mergedUser: SystemUser = {
        // Legacy fields mapped from new schema
        auth0Id: null,
        id: profile.id,
        email: profile.email,
        name: fullName,
        personId: profile.id,
        person: person as any,
        roles,
        slackId: profile.slack_id,
        createdAt: profile.created_at,
        updatedAt: profile.updated_at,
        deletedAt: profile.deleted_at,
        // Auth user fields
        given_name: profile.first_name,
        family_name: profile.last_name,
        picture: profile.picture_url,
        email_verified: authUser.email_confirmed_at != null,
        // Extras
        isLoading: false,
        needsOnboarding,
      };

      const username = getUsername(mergedUser);
      const initials = getInitials(mergedUser);

      setUser({
        ...mergedUser,
        username,
        initials,
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
      setUser((currentUser) => ({ ...currentUser, isLoading: false }));
    }
  }, [authUser]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchProfile();
    }

    if (!isAuthenticated && !authLoading) {
      setUser({ ...defaultState.profile, isLoading: false });
    }
  }, [isAuthenticated, authLoading, fetchProfile]);

  const isAdmin = user.roles?.some(
    ({ value }) => value === AvailableRoles.PEOPLE_ADMIN || value === AvailableRoles.HIRING_ADMIN
  );

  return (
    <userContext.Provider value={{ profile: user, refresh: fetchProfile, isAdmin }}>
      {children}
    </userContext.Provider>
  );
}

export default function useProfile(): UserContext {
  return useContext(userContext);
}
